import { type FC, type ReactNode } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { Link } from 'dibs-elements/exports/Link';
import ChevronLeft from 'dibs-icons/exports/ChevronLeft';
import ChevronRight from 'dibs-icons/exports/ChevronRight';

import { ScrollContainer } from 'dibs-scroll-carousel/exports/ScrollContainer';
import { useInternalScrollCarousel } from 'dibs-scroll-carousel/exports/InternalScrollCarouselContext';

import { type TileSize } from '../constants';

import dibsCss from 'dibs-css';
import styles from '../styles/styles.scss';

export type Props = {
    children: ReactNode[];
    size: TileSize;
    showArrows: boolean;
    showSwiper: boolean;
    setIsSwiperDismissed: (isSwiperDismissed: boolean) => void;
};

export const ProductCarousel: FC<Props> = ({
    children,
    size,
    showArrows,
    showSwiper,
    setIsSwiperDismissed,
}) => {
    const { currentIndex, setCurrentIndex } = useInternalScrollCarousel();
    const commonClasses = classnames(dibsCss.absolute, dibsCss.zBump, dibsCss.top50);
    const arrowClasses = classnames(commonClasses, styles.arrowLink, {
        [styles.arrowLinkLarge]: size === 'large',
    });
    const swiperClasses = classnames(
        commonClasses,
        styles.swipeIndicator,
        dibsCss.right0,
        dibsCss.textBodySmallHeavy,
        dibsCss.textNoir,
        dibsCss.flex,
        dibsCss.itemsCenter,
        dibsCss.hLarge,
        dibsCss.plSmaller,
        dibsCss.prXxsmall
    );
    return (
        <div className={classnames(dibsCss.wFull, dibsCss.hFull, dibsCss.relative)}>
            {showArrows && (
                <>
                    <Link
                        className={classnames(arrowClasses, dibsCss.left0)}
                        onClick={() => setCurrentIndex(currentIndex - 1)}
                    >
                        <ChevronLeft variation="24" dataTn="arrow-left" />
                    </Link>

                    <Link
                        className={classnames(arrowClasses, dibsCss.right0)}
                        onClick={() => setCurrentIndex(currentIndex + 1)}
                    >
                        <ChevronRight variation="24" dataTn="arrow-right" />
                    </Link>
                </>
            )}
            {showSwiper && (
                <VisibilityTracker
                    className={swiperClasses}
                    watchAfterFirstVisible
                    onVisibilityChange={({ boundingClientRect, disconnect }) => {
                        if (boundingClientRect?.top < 0) {
                            // hide swipe indicator if user scrolled past it
                            setIsSwiperDismissed(true);
                            disconnect();
                        }
                    }}
                >
                    <FormattedMessage defaultMessage="Swipe" id="productTile.swipeIndicatorText" />
                    <ChevronRight variation="20" />
                </VisibilityTracker>
            )}
            <ScrollContainer>{children}</ScrollContainer>
        </div>
    );
};
